import { isFunction } from 'lodash';
import { useCallback } from 'react';

import { IdentityDocumentType as IdentityDocumentTypeApi } from '@dreryk/egabinet-patient-published-language-api';

import { assertUnreachable, FieldControllerProps, useTranslate } from '@dreryk/react-common';
import { Select } from '@dreryk/react-components';

import { identityDocumentTranslationKeys } from '../../../../../../../translations';

export type IdentityDocumentType = 'Passport';
const IDENTITY_DOCUMENT_TYPES: IdentityDocumentType[] = ['Passport'];

export type IdentityDocumentTypeVisitor = <ACCEPT>(
   visitor: ((type: IdentityDocumentType) => ACCEPT) | Record<`visit${IdentityDocumentType}`, () => ACCEPT>
) => ACCEPT;

export const createIdentityDocumentTypeVisitor = (
   identityDocumentType: IdentityDocumentType
): IdentityDocumentTypeVisitor => (visitor) => {
   if (isFunction(visitor)) {
      return visitor(identityDocumentType);
   }

   switch (identityDocumentType) {
      case 'Passport':
         return visitor.visitPassport();
      default:
         return assertUnreachable(identityDocumentType);
   }
};

export const getIdentityDocumentType = (visitor: IdentityDocumentTypeVisitor): IdentityDocumentTypeApi => {
   return visitor({
      visitPassport: () => 'PASSPORT',
   });
};

const useTriggerLabelExtractor = (): ((type?: IdentityDocumentType | null) => string) => {
   const translate = useTranslate();
   return useCallback(
      (type) => {
         return type ? translate(identityDocumentTranslationKeys.type.selectItemName[type]) : '';
      },
      [translate]
   );
};

export type OnIdentityDocumentTypeSelect = (type: IdentityDocumentType | null) => void;

type IdentityDocumentTypeFieldProps = FieldControllerProps<'identityDocumentType', IdentityDocumentType> & {
   readonly onSelect: OnIdentityDocumentTypeSelect;
};

export const IdentityDocumentTypeField = (props: IdentityDocumentTypeFieldProps): JSX.Element => {
   return (
      <Select<IdentityDocumentType>
         testId={props.name}
         label={identityDocumentTranslationKeys.type.label}
         triggerLabelExtractor={useTriggerLabelExtractor()}
         controller={props.register(props.name, {
            required: props.required,
         })}
         defaultValue={props.defaultValue}
         onSelect={props.onSelect}
         disabled
      >
         <Select.List<IdentityDocumentType> items={IDENTITY_DOCUMENT_TYPES}>
            {(documentType) => (
               <Select.Item
                  testId={documentType}
                  item={documentType}
                  label={identityDocumentTranslationKeys.type.selectItemName[documentType]}
               />
            )}
         </Select.List>
      </Select>
   );
};
